import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { GlobalStyle, colors, Container, Button, Section } from './styles/GlobalStyles';
import { HomePage } from './components/HomePage';
import { RegistrationPage } from './components/RegistrationPage';
import ConfigDebug from './components/ConfigDebug';
import { configService } from './services/configService';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import ScanQrRoute from './routes/ScanQrRoute';
import AdminRoute from './routes/AdminRoute';

const ClosedSection = styled(Section)`
  background: ${colors.cream};
  min-height: 100vh;
  display: flex;
  align-items: center;
`;

const ClosedCard = styled(motion.div)`
  background: white;
  border-radius: 2px;
  padding: 3rem;
  border: 1px solid ${colors.secondary};
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
`;

const BackButton = styled(Button)`
  background: ${colors.brown};
  color: white;
  margin-bottom: 2rem;

  &:hover {
    background: ${colors.darkRed};
  }
`;

const NotifyForm = styled.form`
  background: ${colors.white};
  border: 1px solid ${colors.secondary};
  border-radius: 2px;
  padding: 20px;
  margin: 20px 0;
`;

const NotifyInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid ${colors.secondary};
  border-radius: 2px;
  font-size: 1rem;
  margin-bottom: 15px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${colors.primary};
  }
`;

const NotifyButton = styled(Button)`
  background: ${colors.primary};
  width: 100%;
  margin-bottom: 10px;

  &:hover {
    background: ${colors.darkRed};
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

interface ClosedPageProps {
  message: string;
  onBackClick: () => void;
}

const ClosedPage: React.FC<ClosedPageProps> = ({ message, onBackClick }) => {
  const [contact, setContact] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleNotifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!contact.trim()) return;
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${configService.getBackendUrl()}/api/registration/notify-when-open`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contact: contact.trim(),
          timestamp: new Date().toISOString(),
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setContact('');
      } else {
        throw new Error('Errore nell\'invio');
      }
    } catch (error) {
      console.error('Errore:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 3000);
    }
  };

  return (
    <ClosedSection>
      <Container>
        <BackButton onClick={onBackClick}>
          ⬅️ Torna alla Home
        </BackButton>
        
        <ClosedCard
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
        >
          <div style={{ fontSize: '4rem', marginBottom: '20px' }}>⏳</div>
          
          <div style={{ 
            background: '#fff3cd', 
            border: '2px solid #ffc107', 
            borderRadius: '8px', 
            padding: '20px', 
            marginBottom: '30px' 
          }}>
            <p style={{ 
              fontSize: '1.2rem', 
              color: '#856404', 
              margin: 0,
              lineHeight: '1.6'
            }}>
              {message}
            </p>
          </div>

          <NotifyForm onSubmit={handleNotifySubmit}>
            <h3 style={{ color: colors.brown, marginBottom: '15px', textAlign: 'center' }}>
              🔔 Vuoi essere avvisato quando riaprono le iscrizioni?
            </h3>
            <NotifyInput
              type="text"
              placeholder="Inserisci la tua email o numero di telefono"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              disabled={isSubmitting}
            />
            <NotifyButton type="submit" disabled={isSubmitting || !contact.trim()}>
              {isSubmitting ? 'Invio in corso...' : 'Avvisami quando aprono le iscrizioni'}
            </NotifyButton>
            
            {submitStatus === 'success' && (
              <div style={{ 
                background: '#d4edda', 
                color: '#155724', 
                padding: '10px', 
                borderRadius: '5px', 
                fontSize: '0.9rem',
                textAlign: 'center'
              }}>
                ✅ Perfetto! Ti contatteremo al più presto! Hai tempo per coinvolgere altri amici!
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div style={{ 
                background: '#f8d7da', 
                color: '#721c24', 
                padding: '10px', 
                borderRadius: '5px', 
                fontSize: '0.9rem',
                textAlign: 'center'
              }}>
                ❌ Errore nell'invio. Riprova più tardi.
              </div>
            )}
          </NotifyForm>

          <p style={{ fontSize: '1.1rem', color: colors.brown, marginBottom: '30px' }}>
            Per informazioni puoi contattarci:
          </p>
          <div style={{ 
            background: colors.cream, 
            padding: '20px', 
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <p style={{ margin: '5px 0', color: colors.brown }}>
              <strong>📧 Email:</strong> ariadifest@gmail.com
            </p>
            <p style={{ margin: '5px 0', color: colors.brown }}>
              <strong>📞 Telefono:</strong> Marisa 3497497547
            </p>
          </div>
          <Button onClick={onBackClick}>
            Torna alla Home
          </Button>
        </ClosedCard>
      </Container>
    </ClosedSection>
  );
};

function App() {
  const [currentPage, setCurrentPage] = useState<'home' | 'registration' | 'closed'>('home');
  const [closureMessage, setClosureMessage] = useState<string>('');

  const handleRegisterClick = () => {
    setCurrentPage('registration');
  };

  const handleShowClosureMessage = (message: string) => {
    setClosureMessage(message);
    setCurrentPage('closed');
  };

  const handleBackClick = () => {
    setCurrentPage('home');
  };

  return (
    <Router>
      <GlobalStyle />
      <Routes>
        <Route path="/" element={
          <>
            {currentPage === 'home' && (
              <HomePage 
                onRegisterClick={handleRegisterClick} 
                onShowClosureMessage={handleShowClosureMessage}
              />
            )}
            {currentPage === 'registration' && (
              <RegistrationPage onBackClick={handleBackClick} onShowClosureMessage={handleShowClosureMessage} />
            )}
            {currentPage === 'closed' && <ClosedPage message={closureMessage} onBackClick={handleBackClick} />}
          </>
        } />
        <Route path="/scan_qr" element={<ScanQrRoute />} />
        <Route path="/admin" element={<AdminRoute />} />
      </Routes>
    </Router>
  );
}

export default App;
